import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, Inject, OnInit, signal } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	NavigationEnd,
	NavigationStart,
	Router,
	RouterOutlet,
	UrlSegment
} from '@angular/router';
import { animate, style, transition, trigger } from '@angular/animations';
import {
	AlarisCardComponent,
	AlarisContentLayoutService,
	AlarisLanguageService,
	AlarisMenuSidebarComponent,
	AlarisNavbarComponent,
	AlarisProfileComponent,
	AlarisRootContentComponent,
	LAYOUT_SERVICE_INJECTOR,
	RouterDataLinks
} from '@campaign-portal/components-library';

import { routerLinks } from '@helpers/shared/router-links.const';
import { AuthGuardService } from '../../auth/auth.guard';
import { AuthService } from '../../auth/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../../auth/user.service';
import { TranslateModule } from '@ngx-translate/core';
import { HlrConfigService } from '@helpers/providers/hlr-config.service';
import { filter, map } from 'rxjs';
import { DsBreadcrumbsComponent, DsBreadcrumbsItem } from '@alarislabs/angular-components/breadcrumbs';

function mainLayoutServiceFact(elementRef: ElementRef, router: Router): AlarisContentLayoutService {
	return new AlarisContentLayoutService(elementRef.nativeElement, router);
}

@Component({
	selector: 'app-main-layout',
	templateUrl: './main-layout.component.html',
	styleUrls: ['./main-layout.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: LAYOUT_SERVICE_INJECTOR, useFactory: mainLayoutServiceFact, deps: [ElementRef, Router] }],
	animations: [
		trigger('mainLayoutChanged', [
			transition('* <=> *', [
				style({ height: 0, opacity: 0 }),
				animate('500ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '*', opacity: 1 })),
			]),
		]),
		trigger('inOutAnimation', [
			transition(':enter', [
				style({ width: 0, opacity: 0 }),
				animate('150ms ease-out', style({ width: 240, opacity: 1 })),
			]),
			transition(':leave', [
				style({ width: 240, opacity: 1 }),
				animate('150ms ease-in', style({ width: 0, opacity: 0 })),
			]),
		]),
	],
	imports: [
		AlarisMenuSidebarComponent,
		AlarisRootContentComponent,
		AlarisNavbarComponent,
		AlarisProfileComponent,
		RouterOutlet,
		TranslateModule,
		AlarisCardComponent,
		DsBreadcrumbsComponent,
	],
})
export class MainLayoutComponent implements OnInit {
	readonly globals = window.frames?.frameInVoice?.Globals;
	dynamicTitle = '';
	readonly breadcrumbs = signal<DsBreadcrumbsItem[]>([]);

	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly authGuard = inject(AuthGuardService);
	private readonly users = inject(UserService);
	private readonly lService = inject(AlarisLanguageService);

	constructor(
		@Inject(LAYOUT_SERVICE_INJECTOR) public readonly layoutService: AlarisContentLayoutService,
		public readonly config: HlrConfigService,
		public readonly auth: AuthService,
	) {
		this.router.events
			.pipe(
				filter((e) => e instanceof NavigationStart || e instanceof NavigationEnd),
				map((e) => {
					if (e instanceof NavigationStart) {
						this.dynamicTitle = this.router.currentNavigation()?.extras?.state?.['title'];
					}

					if (e instanceof NavigationEnd) {
						const routeChain = this.getRouteDataChain();
						const urlSegments = e.urlAfterRedirects.split('/');

						const breadcrumbs: DsBreadcrumbsItem[] = urlSegments.map((segment, index) => {
							const isLast = index === urlSegments.length - 1;
							return {
								label: routeChain.length > index ? this.lService.translate(routeChain[index]) : segment,
								type: isLast ? 'text' : 'link',
								path: urlSegments.slice(0, index + 1).join('/') || '/',
							};
						});

						this.breadcrumbs.set(breadcrumbs);
					}
					return e;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	ngOnInit(): void {
		if (this.router.url === '/' || this.router.url === '') {
			const defaultRoute = this.getFirstAvailableTopLevelRoute();
			if (defaultRoute) {
				this.router.navigateByUrl(defaultRoute, { replaceUrl: true });
			}
		}
	}

	menuList = computed(() => {
		const userPerms = this.users.userSignal$().permissions ?? [];
		const result: Partial<RouterDataLinks> = {};

		for (const routeName in routerLinks) {
			const item = routerLinks[routeName];

			if (item.children && item.children.length > 0) {
				const allowedChildren = item.children.filter((child) =>
					child.data.permissions.every((p) => userPerms.includes(p))
				);

				if (allowedChildren.length > 0) {
					result[routeName] = {
						...item,
						children: allowedChildren,
					};
				}
			} else {
				const parentPermissions = item.data?.permissions || [];
				if (parentPermissions.every((p) => userPerms.includes(p))) {
					result[routeName] = { ...item };
				}
			}
		}

		return result as Readonly<RouterDataLinks>;
	});

	prepareInterfaceRoute(outlet: RouterOutlet): boolean {
		return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animationState'];
	}

	getRouteDataChain = computed((): string[] => {
		const dataChain: string[] = [];
		let currentRoute: ActivatedRouteSnapshot | null = this.authGuard.route();

		while (currentRoute) {
			if (currentRoute.data && Object.keys(currentRoute.data).length !== 0) {
				dataChain.push(currentRoute.data['title'] ?? 'gl.alaris_labs');
			}
			currentRoute = currentRoute.parent;
		}
		return dataChain.reverse();
	});

	getBackRoutePath = computed((): UrlSegment[] => {
		const segments: UrlSegment[] = [];
		let parent = this.authGuard.route()?.parent;
		while (parent) {
			segments.push(...parent.url);
			parent = parent.parent;
		}
		return segments.reverse();
	});

	getBackRoute = computed((): boolean => {
		return this.authGuard.route()?.data['backRoute'];
	});

	getTitle = computed((): string => {
		let current: ActivatedRouteSnapshot | null = this.authGuard.route();

		while (current) {
			const title = current.data?.['title'];
			const permissions = current.data?.['permissions'] ?? [];

			const hasAccess = this.users.allowed$(permissions)();

			if (title && hasAccess) {
				return this.dynamicTitle || title;
			}

			current = current.parent;
		}

		return this.dynamicTitle || 'gl.alaris_labs';
	});

	getOverflow = computed((): boolean => {
		return this.authGuard.route()?.data['overflow'];
	});

	private getFirstAvailableTopLevelRoute(): string | null {
		const order = Object.keys(routerLinks) as (keyof typeof routerLinks)[];
		for (const key of order) {
			const route = routerLinks[key];
			if (!route) continue;
			const parentPermissions = route.data?.permissions || [];
			const parentAccessible = parentPermissions.length === 0 || parentPermissions.every(p => this.users.allowed([p]));
			if (!parentAccessible) continue;

			if (route.children && route.children.length) {
				for (const child of route.children) {
					const childPermissions = child.data?.permissions || [];
					const childAccessible = childPermissions.length === 0 || childPermissions.every(p => this.users.allowed([p]));
					if (childAccessible && child.path && child.path !== '') {
						return `/${route.path}/${child.path}`;
					}
				}
				continue;
			} else {
				return `/${route.path}`;
			}
		}
		return '/';
	}
}